import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { Site } from "@/lib/api/client";
import { useSites } from "@/lib/hooks/use-sites";

/**
 * The site scope every section reads (ADR 013, principle 2).
 *
 * One control in the header - all sites, or one - kept in the URL as
 * `?site=<id>` so a link carries its scope, and the only reader and writer of
 * that parameter. It replaced four per-page site filters, each of which read
 * and wrote the URL on its own.
 *
 * The Explorer keeps its multi-site `siteId` list as a refinement while the
 * scope is "all sites"; nothing else does.
 */

export const SITE_SCOPE_PARAM = "site";

/**
 * Parameters that name something belonging to the site in scope. Changing
 * site invalidates them, so they are dropped with it rather than left to
 * open a drawer on another site's row.
 */
const SITE_SCOPED_PARAMS = ["signature", "script"] as const;

export interface SiteScope {
  /** The site in scope, or undefined for all of them. */
  siteId: string | undefined;
  site: Site | undefined;
  /**
   * Whether the reader chose this site, rather than it being the only one
   * they have. A filter that can hide rows belonging to no site should
   * follow a choice, not an accident of having one site.
   */
  isExplicit: boolean;
  sites: Site[];
  isLoading: boolean;
  setSiteId: (siteId: string | undefined) => void;
}

export function useSiteScope(): SiteScope {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading } = useSites();
  const sites = useMemo(() => data?.sites ?? [], [data?.sites]);

  // A shared link can name a site this tenant no longer has, or never had:
  // that reads as all sites rather than as an empty view of nothing.
  //
  // A tenant with one site is always scoped to it: "all sites" and "that
  // site" show the same thing, and the sections that need a site - the
  // reliability tabs, the scripts views - would otherwise have none.
  const { site, isExplicit } = useMemo(() => {
    const id = searchParams.get(SITE_SCOPE_PARAM);
    const named = id
      ? sites.find((candidate) => candidate.id === id)
      : undefined;
    return {
      site: named ?? (sites.length === 1 ? sites[0] : undefined),
      isExplicit: named !== undefined,
    };
  }, [searchParams, sites]);

  const setSiteId = useCallback(
    (siteId: string | undefined) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (siteId) params.set(SITE_SCOPE_PARAM, siteId);
        else params.delete(SITE_SCOPE_PARAM);
        for (const key of SITE_SCOPED_PARAMS) params.delete(key);
        return params;
      });
    },
    [setSearchParams],
  );

  return { siteId: site?.id, site, isExplicit, sites, isLoading, setSiteId };
}

/**
 * The scope as a section link should carry it, so that moving from Policy to
 * Headers keeps the site the reader chose. Sidebar and tab links are built
 * with this rather than with a bare href.
 */
export function withSiteScope(
  href: string,
  siteId: string | undefined,
): string {
  return siteId
    ? `${href}?${SITE_SCOPE_PARAM}=${encodeURIComponent(siteId)}`
    : href;
}
